/**
 * Lookup keys for a card name: the full name, plus the front face when the
 * name is a double-faced "Front // Back".
 *
 * Sources disagree on DFCs: the builder may say "Bala Ged Recovery" where
 * the analysis says "Bala Ged Recovery // Bala Ged Sanctuary". So anything
 * that matches cards by name indexes them under BOTH keys and tries both on
 * lookup. Extracted from buildAnalysisAdapter, which held the only copy.
 */
export function nameKeys(name: string): string[] {
  const base = name.toLowerCase().trim()
  const front = base.split(' // ')[0].trim()
  return front && front !== base ? [base, front] : [base]
}

/**
 * EVERY face of a card name, plus the full name: for "Front // Back" that is
 * the full string, the front, and the back.
 *
 * `nameKeys` deliberately stops at the front face because it indexes the
 * builder's synergy map, where the front face is the canonical key. Identity
 * comparison needs more than that: a decklist can name a modal DFC by its
 * BACK face (a player writing "Tibalt, Cosmic Impostor" for the card the
 * analysis calls "Valki, God of Lies // Tibalt, Cosmic Impostor"), and the
 * command zone comes from what the USER typed, not from a canonical source.
 * Matching front-only let that commander through every exclusion and sent it
 * back to the builder as an include_card.
 */
export function allFaceKeys(name: string): string[] {
  const base = name.toLowerCase().trim()
  const faces = base.split(' // ').map(f => f.trim()).filter(Boolean)
  return [...new Set([base, ...faces])]
}

/** True when two card names refer to the same card on ANY face, in either
 *  direction. Use this for identity, never `nameKeys`. */
export function sameCard(a: string, b: string): boolean {
  const left = allFaceKeys(a)
  return allFaceKeys(b).some(k => left.includes(k))
}
